import { useState, useEffect, useCallback } from 'react';
import { usePenpal } from './usePenpal';
import {
  LATEST_CHANGELOG_VERSION,
  CHANGELOG_STORAGE_KEY,
  CHANGELOG_LOCAL_STORAGE_KEY,
} from '../components/changelog/changelog';

function parseLastSeen(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

export function useChangelogSeen() {
  const { isInIframe, getSettings, setSettings } = usePenpal();
  /** null = not loaded yet, true/false after we've read storage */
  const [hasNewChanges, setHasNewChanges] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        if (isInIframe && getSettings) {
          const settings = await getSettings();
          const raw = (settings as Record<string, unknown>)?.[CHANGELOG_STORAGE_KEY];
          const lastSeen = parseLastSeen(raw);
          if (!cancelled) {
            setHasNewChanges(lastSeen !== LATEST_CHANGELOG_VERSION);
          }
        } else {
          const stored = typeof window !== 'undefined' ? localStorage.getItem(CHANGELOG_LOCAL_STORAGE_KEY) : null;
          const lastSeen = stored != null ? parseLastSeen(stored) : null;
          if (!cancelled) {
            setHasNewChanges(lastSeen !== LATEST_CHANGELOG_VERSION);
          }
        }
      } catch {
        if (!cancelled) setHasNewChanges(true);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isInIframe, getSettings]);

  const markAsSeen = useCallback(async () => {
    try {
      if (isInIframe && setSettings) {
        await setSettings(CHANGELOG_STORAGE_KEY, LATEST_CHANGELOG_VERSION);
      } else if (typeof window !== 'undefined') {
        localStorage.setItem(CHANGELOG_LOCAL_STORAGE_KEY, String(LATEST_CHANGELOG_VERSION));
      }
      setHasNewChanges(false);
    } catch {
      // keep dot visible on error
    }
  }, [isInIframe, setSettings]);

  return { hasNewChanges, markAsSeen };
}
